import { useState } from "react";
import { useResource } from "@/lib/api";
import { formatAUD, isNegative } from "@/lib/format";
import FyTabs from "@/components/FyTabs";
import CashflowChart from "@/components/cashflow/CashflowChart";
import MonthlyTable from "@/components/cashflow/MonthlyTable";
import YoyStrip from "@/components/cashflow/YoyStrip";
import CategorySummary from "@/components/cashflow/CategorySummary";

const Stat = ({ label, value, tone, testId }) => (
  <div className="border border-border bg-card p-5">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-mono text-lg sm:text-2xl mt-2 break-all ${tone || ""}`} data-testid={testId}>{formatAUD(value)}</div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mt-12">
    <h2 className="font-display text-base md:text-lg font-semibold tracking-tight mb-4">{title}</h2>
    {children}
  </section>
);

export default function CashflowPage() {
  const { data: fys } = useResource("/financial_years");
  const [fy, setFy] = useState(null);
  const { data, error } = useResource(fy ? `/cashflow?fy=${fy}` : null);

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="cashflow-title">Cashflow</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Monthly income, expenditure and net cash, July to June.</p>
      <div className="mt-8"><FyTabs fys={fys} value={fy} onChange={setFy} prefix="cashflow" /></div>
      {error && <p className="mt-6 text-destructive text-sm">{error}</p>}
      {data && data.fy === fy && (
        <>
          <div className="mt-6 grid gap-4 grid-cols-2 lg:grid-cols-4">
            <Stat label={`Income ${data.fy}`} value={data.totals.income} tone="text-emerald-300" testId="cashflow-stat-income" />
            <Stat label="Expenditure" value={data.totals.expenditure} tone="text-rose-300" testId="cashflow-stat-expenditure" />
            <Stat label="FY net" value={data.totals.net} tone={isNegative(data.totals.net) ? "text-rose-300" : ""} testId="cashflow-stat-net" />
            <Stat label="Forecast" value={data.totals.forecast} tone="text-amber-300" testId="cashflow-stat-forecast" />
          </div>
          <div className="mt-6"><CashflowChart months={data.months} prevFy={data.prev_fy} /></div>
          <Section title="Monthly summary"><MonthlyTable months={data.months} totals={data.totals} prevFy={data.prev_fy} /></Section>
          <Section title="Year on year"><YoyStrip yoy={data.yoy} current={data.fy} onPick={setFy} /></Section>
          <Section title={`Category summary ${data.fy}`}><CategorySummary categories={data.categories} blankRows={data.blank_rows} /></Section>
        </>
      )}
    </div>
  );
}
